/* eslint-disable import/order */
import './scss/style.scss';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Navigate, Route } from 'react-router-dom';
import Loader from 'components/global/Loader';
import PublicRoute from 'components/global/Routes/PublicRoute';
import PrivateRoute from 'components/global/Routes/PrivateRoute';
import { $app, $auth, $user } from 'signals/global.signals';
import Notifier from 'components/global/Notifier/Notifier';
import initApp from 'utils/initApp';
import initChartJs from 'utils/initChartJs';
import injectClarity from 'utils/injectClarity';

// Public Routes
import Login from './components/views/Login';
import Signup from './components/views/Signup';
import Signout from './components/views/Signout';
import PublicRoom from './components/views/PublicRoom';
import PublicAcceptInvite from 'components/views/PublicAcceptInvite/PublicAcceptInvite';

// Onboarding Route
import Onboarding from 'components/views/Onboarding/Onboarding';

// Investor Routes
import InvestorPortfolioList from 'components/views/InvestorPortfolioList';
import InvestorSettings from './components/views/InvestorSettings';
import InvestorTeam from './components/views/InvestorTeam';
import InvestorDashboard from './components/views/InvestorDashboard';
import InvestorEditProfile from './components/views/InvestorEditProfile';
import InvestorDealflow from 'components/views/InvestorDealflow';

// Founder Routes
import FounderCreateDeal from 'components/views/FounderCreateDeal';
import FounderInvestors from 'components/views/FounderInvestors';
import FounderInvestorDetail from 'components/views/FounderInvestorDetail';
import FounderDashboard from './components/views/FounderDashboard';
import FounderEditProfile from './components/views/FounderEditProfile';
import FounderSettings from './components/views/FounderSettings';
import FounderTeam from './components/views/FounderTeam';
import FounderDeals from './components/views/FounderDeals';
import FounderDeal from './components/views/FounderDeal';
import FounderVault from './components/views/FounderVault';
import FounderRooms from './components/views/FounderRooms';
import OnboardingLayout from 'components/global/Layouts/Onboarding.layout';
import FounderRoom from 'components/views/FounderRoom';
import FounderPipeline from 'components/views/FounderPipeline';
import InvestorPortfolioDetail from 'components/views/InvestorPortfolioDetail';
import Admin from 'components/views/admin';
initApp.registerFirebaseAuthListener();
initApp.registerUserback();
initChartJs();

// Define fallback component outside render
const FallbackComponent = () => {
  if ($user.value.isCreatingUser) {
    return null;
  }
  if (!$auth.value || !$user.value) {
    return <Navigate to="/signout" replace />;
  }
  if ($auth.value.isSignedIn) {
    if ($user.value.userType) {
      return <Navigate to={`/${$user.value.userType}/dashboard`} replace />;
    }
    return <Navigate to="/on-boarding" replace />;
  }
  return <Navigate to="/login" replace />;
};

const App = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      injectClarity('o48hjdc80t');
    }
  }, []);

  if ($app.value.isLoading) {
    return (
      <div className="min-vh-100 w-100 d-flex bg-dark justify-content-center align-items-center flex-grow-1">
        <div>
          <Loader message="" className="text-center" />
          <div className="text-white">Checking Authentication</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Notifier />
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicRoute component={FallbackComponent} />} />
          <Route path="/login" element={<PublicRoute component={Login} unauthenticatedOnly />} />
          <Route path="/signup" element={<PublicRoute component={Signup} unauthenticatedOnly />} />
          <Route path="/room/:id" element={<PublicRoute component={PublicRoom} unauthenticatedOnly={false} />} />
          <Route path="/signout" element={<PublicRoute component={Signout} />} />
          <Route path="/invite/:token" element={<PublicRoute component={PublicAcceptInvite} unauthenticatedOnly />} />

          <Route path="/admin" element={<PrivateRoute component={Admin} userTypeAllowed="admin" />} />


          {/* Onboarding Routes */}
          <Route path="/on-boarding" element={<PrivateRoute component={Onboarding} userTypeAllowed="any" Layout={OnboardingLayout} />} />

          {/* Investor Routes */}
          <Route path="/investor/dashboard" element={<PrivateRoute component={InvestorDashboard} userTypeAllowed="investor" />} />
          <Route path="/investor/portfolio" element={<PrivateRoute component={InvestorPortfolioList} userTypeAllowed="investor" />} />
          <Route path="/investor/portfolio/:id" element={<PrivateRoute component={InvestorPortfolioDetail} userTypeAllowed="investor" />} />
          <Route path="/investor/dealflow" element={<PrivateRoute component={InvestorDealflow} userTypeAllowed="investor" />} />
          <Route path="/investor/settings" element={<PrivateRoute component={InvestorSettings} userTypeAllowed="investor" />} />
          <Route path="/investor/team" element={<PrivateRoute component={InvestorTeam} userTypeAllowed="investor" />} />
          <Route path="/investor/profile/:id" element={<PrivateRoute component={InvestorEditProfile} userTypeAllowed="investor" />} />

          {/* Founder Routes */}
          <Route path="/founder/dashboard" element={<PrivateRoute component={FounderDashboard} userTypeAllowed="founder" />} />
          <Route path="/founder/settings" element={<PrivateRoute component={FounderSettings} userTypeAllowed="founder" />} />
          <Route path="/founder/team" element={<PrivateRoute component={FounderTeam} userTypeAllowed="founder" />} />
          <Route path="/founder/pipelines" element={<PrivateRoute component={FounderPipeline} userTypeAllowed="founder" />} />
          <Route path="/founder/investors" element={<PrivateRoute component={FounderInvestors} userTypeAllowed="founder" />} />
          <Route path="/founder/investors/:id" element={<PrivateRoute component={FounderInvestorDetail} userTypeAllowed="founder" />} />
          <Route path="/founder/profile/:id" element={<PrivateRoute component={FounderEditProfile} userTypeAllowed="founder" />} />
          <Route path="/founder/deals" element={<PrivateRoute component={FounderDeals} userTypeAllowed="founder" />} />
          <Route path="/founder/deal/create" element={<PrivateRoute component={FounderCreateDeal} userTypeAllowed="founder" />} />
          <Route path="/founder/deal/:id" element={<PrivateRoute component={FounderDeal} userTypeAllowed="founder" />} />
          <Route path="/founder/vault" element={<PrivateRoute component={FounderVault} userTypeAllowed="founder" />} />
          <Route path="/founder/rooms" element={<PrivateRoute component={FounderRooms} userTypeAllowed="founder" />} />
          <Route path="/founder/room/create" element={<PrivateRoute component={FounderRooms} userTypeAllowed="founder" />} />
          <Route path="/founder/room/:id" element={<PrivateRoute component={FounderRoom} userTypeAllowed="founder" />} />


          {/* Catch-all route */}
          <Route path="*" element={<FallbackComponent />} />
        </Routes>
      </Router>
    </>
  );
};

export default App;
